import { createFileRoute } from "@tanstack/react-router";
import React, { useState } from "react";
import {
  Building2,
  CheckCircle2,
  ExternalLink,
  FileText,
  Globe,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Plus,
  Trash2,
  User,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/shared/PageHeader";
import StatusChip from "@/components/shared/StatusChip";
import SectionCard from "@/components/shared/SectionCard";
import EmptyState from "@/components/shared/EmptyState";
import { FullPageState } from "@/components/shared/FullPageState";
import { IconAction } from "@/components/shared/IconAction";
import { useAdminPartners, useApprovePartner, useRejectPartner, useUpdatePartner, useAdminPlacementSlots, useAdminCreateSlot, useAdminUpdateSlot, useAdminDeleteSlot } from "@/hooks/useAdminData";
import { useSkills, useRegions } from "@/hooks/useReferenceData";
import { getFileUrl } from "@/lib/api-client";
import { PageSkeleton, ListSkeleton } from "@/components/shared/Skeletons";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "@/components/forms";
import {  } from "@/components/admin/people-ui";
import { MultiCombobox } from "@/components/ui/multi-combobox";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/admin/partners/$id")({
  component: AdminPartnerDetailPage,
});

function AdminPartnerDetailPage() {
  const { id } = Route.useParams();
  const { data: partnersData = [], isLoading } = useAdminPartners();
  const approvePartner = useApprovePartner();
  const rejectPartner = useRejectPartner();
  const updatePartner = useUpdatePartner();

  const p: any = partnersData.find((x: any) => String(x.id) === id) || partnersData[0];

  const { data: partnerSlots = [], isLoading: slotsLoading } = useAdminPlacementSlots(p?.user_id);
  const createSlot = useAdminCreateSlot();
  const updateSlot = useAdminUpdateSlot();
  const deleteSlot = useAdminDeleteSlot();

  const { data: skillsData } = useSkills();
  const { data: regions } = useRegions();

  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editData, setEditData] = useState<Record<string, string>>({});

  const [slotDialogOpen, setSlotDialogOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<any>(null);
  const [slotForm, setSlotForm] = useState({
    title: "",
    description: "",
    requirements: [] as string[],
    location: "",
    duration: "",
    arrangement: "on-site",
    max_students: 1,
  });

  if (isLoading) {
    return <PageSkeleton />;
  }

  if (!p) {
    return (
      <FullPageState
        icon={<Building2 />}
        title="Partner not found"
        description="This partner record does not exist or has been removed."
      />
    );
  }

  const slots = (partnerSlots as any[]).reduce((sum: number, s: any) => sum + (s.max_students || 0), 0);
  const filled = (partnerSlots as any[]).reduce((sum: number, s: any) => sum + (s.filled_slots || 0), 0);
  const fillRate = slots > 0 ? (filled / slots) * 100 : 0;

  const verificationDocs = p.verification_docs
    ? p.verification_docs.split(",").filter((d: string) => d.trim())
    : [];

  const handleReject = () => {
    const userId = p.user_id;
    if (!userId) return;
    rejectPartner.mutate(userId, {
      onSettled: () => {
        setRejectDialogOpen(false);
        setRejectReason("");
      },
    });
  };

  const handleApprove = () => {
    const userId = p.user_id;
    if (!userId) return;
    approvePartner.mutate(userId);
  };

  const openEditDialog = () => {
    setEditData({
      company_name: p.name || "",
      industry: p.industry || "",
      website: p.website || "",
      location: p.location || "",
      contact_person: p.contact_person || "",
      contact_email: p.contact_email || "",
      contact_phone: p.contact_phone || "",
      description: p.description || "",
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    const userId = p.user_id;
    if (!userId) return;
    updatePartner.mutate(
      { id: userId, payload: editData },
      { onSettled: () => setEditDialogOpen(false) }
    );
  };

  const openAddSlot = () => {
    setEditingSlot(null);
    setSlotForm({ title: "", description: "", requirements: [], location: "", duration: "", arrangement: "on-site", max_students: 1 });
    setSlotDialogOpen(true);
  };

  const openEditSlot = (slot: any) => {
    setEditingSlot(slot);
    const reqs = slot.requirements
      ? slot.requirements.split(",").map((r: string) => r.trim()).filter(Boolean)
      : [];
    setSlotForm({
      title: slot.title || "",
      description: slot.description || "",
      requirements: reqs,
      location: slot.location || "",
      duration: slot.duration || "",
      arrangement: slot.arrangement || "on-site",
      max_students: slot.max_students || 1,
    });
    setSlotDialogOpen(true);
  };

  const handleSaveSlot = async () => {
    const payload = {
      ...slotForm,
      requirements: slotForm.requirements.join(", "),
    };
    try {
      if (editingSlot) {
        await updateSlot.mutateAsync({ id: editingSlot.id, ...payload });
      } else {
        await createSlot.mutateAsync({ partnerId: p.user_id, ...payload });
      }
      setSlotDialogOpen(false);
      toast.success(editingSlot ? "Slot updated." : "Slot created.");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save slot.");
    }
  };

  const handleDeleteSlot = async (slotId: number) => {
    if (window.confirm("Delete this placement slot? This cannot be undone.")) {
      await deleteSlot.mutateAsync(slotId);
    }
  };

  const skillNames: string[] = (skillsData || []).map((s: string | { id: number; name: string }) =>
    typeof s === "string" ? s : s.name,
  );

  const facts: { icon: React.ReactNode; label: string; value: React.ReactNode }[] = [
    { icon: <Building2 />, label: "Industry", value: p.industry || "Not specified" },
    { icon: <MapPin />, label: "Location", value: p.location || "Not specified" },
    { icon: <User />, label: "Contact person", value: p.contact_person || "Not specified" },
    { icon: <Mail />, label: "Email", value: p.contact_email || p.email || "Not specified" },
  ];
  if (p.website) {
    facts.push({
      icon: <Globe />,
      label: "Website",
      value: (
        <a
          href={p.website.startsWith("http") ? p.website : `https://${p.website}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-foreground underline-offset-4 hover:underline"
        >
          {p.website}
        </a>
      ),
    });
  }

  const editFields: { key: string; label: string }[] = [
    { key: "company_name", label: "Organization name" },
    { key: "industry", label: "Industry" },
    { key: "website", label: "Website" },
    { key: "location", label: "Location" },
    { key: "contact_person", label: "Contact person" },
    { key: "contact_email", label: "Contact email" },
    { key: "contact_phone", label: "Contact phone" },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title={p.name}
        breadcrumbs={[{ label: "Partners", path: "/admin/partners" }, { label: p.name }]}
        action={
          <Button variant="outline" onClick={openEditDialog}>
            <Pencil />
            Edit profile
          </Button>
        }
      />

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)]">
        <div className="flex min-w-0 flex-col gap-4">
          <section className="rounded-md border border-border bg-card p-4 md:p-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="flex min-w-0 flex-1 items-center gap-4">
                <Avatar className="size-14 rounded-full">
                  <AvatarFallback className="rounded-full text-lg">
                    {p.name?.charAt(0) || "?"}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <h2 className="truncate text-title text-foreground">{p.name}</h2>
                  <div className="mt-1 flex items-center gap-2">
                    <StatusChip status={p.status} />
                  </div>
                </div>
              </div>
              {p.status === "pending" && (
                <div className="flex gap-2 max-sm:[&>*]:flex-1">
                  <Button
                    size="sm"
                    disabled={approvePartner.isPending}
                    onClick={handleApprove}
                  >
                    <CheckCircle2 />
                    {approvePartner.isPending ? "Verifying..." : "Verify"}
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive-outline"
                    disabled={rejectPartner.isPending}
                    onClick={() => setRejectDialogOpen(true)}
                  >
                    <XCircle />
                    Reject
                  </Button>
                </div>
              )}
            </div>

            <dl className="mt-5 grid grid-cols-1 gap-px overflow-hidden rounded-sm border border-border bg-border sm:grid-cols-2">
              {facts.map((f) => (
                <div key={f.label} className="flex items-start gap-3 bg-card p-3">
                  <span className="mt-0.5 text-muted-foreground [&_svg]:size-4" aria-hidden>
                    {f.icon}
                  </span>
                  <div className="min-w-0">
                    <dt className="eyebrow">{f.label}</dt>
                    <dd className="mt-0.5 text-sm break-words text-foreground">{f.value}</dd>
                  </div>
                </div>
              ))}
              {facts.length % 2 === 1 && <div className="hidden bg-card sm:block" aria-hidden />}
            </dl>

            {p.description && (
              <div className="mt-4">
                <p className="eyebrow">Description</p>
                <p className="mt-1 text-sm leading-6 text-body">{p.description}</p>
              </div>
            )}
          </section>

          <SectionCard title="Verification documents" noPadding>
            {verificationDocs.length > 0 ? (
              <ul className="divide-y divide-border">
                {verificationDocs.map((docPath: string, idx: number) => {
                  const trimmedPath = docPath.trim();
                  const fileName = trimmedPath.split("/").pop() || `Document ${idx + 1}`;
                  const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName);
                  const isPdf = /\.pdf$/i.test(fileName);
                  const fullUrl = getFileUrl(trimmedPath);

                  return (
                    <li
                      key={idx}
                      className="flex min-h-11 items-center gap-3 px-4 py-2.5 md:px-5"
                    >
                      <FileText className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-foreground">{fileName}</p>
                        <p className="text-caption text-muted-foreground">
                          {isPdf ? "PDF Document" : isImage ? "Image" : "File"}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        {isImage && (
                          <Button variant="ghost" size="sm" asChild>
                            <a href={fullUrl} target="_blank" rel="noopener noreferrer">
                              Preview
                            </a>
                          </Button>
                        )}
                        <Button variant="outline" size="sm" asChild>
                          <a href={fullUrl} target="_blank" rel="noopener noreferrer" download>
                            <ExternalLink />
                            Open
                          </a>
                        </Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <EmptyState
                bare
                icon={<FileText />}
                title="No documents yet"
                description="No verification documents uploaded yet"
              />
            )}
          </SectionCard>

          <SectionCard
            title="Placement slots"
            noPadding
            actions={
              <Button size="sm" variant="outline" onClick={openAddSlot}>
                <Plus />
                Add slot
              </Button>
            }
          >
            {slotsLoading ? (
              <div className="px-4 md:px-5">
                <ListSkeleton items={3} />
              </div>
            ) : (partnerSlots as any[]).length === 0 ? (
              <EmptyState
                bare
                icon={<FileText />}
                title="No placement slots created yet"
                description="Add a slot to define hiring capacity for this partner"
              />
            ) : (
              <ul className="divide-y divide-border">
                {(partnerSlots as any[]).map((slot: any) => (
                  <li
                    key={slot.id}
                    className="flex min-h-11 items-center gap-3 px-4 py-2.5 transition-colors hover:bg-muted/60 md:px-5"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground">{slot.title}</p>
                      <p className="truncate text-caption text-muted-foreground">
                        <span className="font-mono tabular-nums">
                          {slot.filled_slots || 0}/{slot.max_students}
                        </span>{" "}
                        positions filled
                        {slot.location ? ` · ${slot.location}` : ""}
                        {slot.arrangement ? ` · ${slot.arrangement}` : ""}
                      </p>
                    </div>
                    <Badge
                      variant={slot.status === "published" ? "success" : "warning"}
                      dot
                      className="capitalize"
                    >
                      {slot.status || "published"}
                    </Badge>
                    <div className="flex items-center gap-0.5">
                      <IconAction
                        label="Edit slot"
                        icon={<Pencil />}
                        onClick={() => openEditSlot(slot)}
                      />
                      <IconAction
                        label="Delete slot"
                        tone="destructive"
                        icon={<Trash2 />}
                        onClick={() => handleDeleteSlot(slot.id)}
                        disabled={deleteSlot.isPending}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </div>

        <aside className="flex min-w-0 flex-col gap-4">
          <SectionCard title="Placement stats" className="h-auto">
            <dl className="grid grid-cols-3 gap-3">
              <div>
                <dt className="eyebrow">Total slots</dt>
                <dd className="mt-1 text-2xl text-foreground tabular-nums">{slots}</dd>
              </div>
              <div>
                <dt className="eyebrow">Filled</dt>
                <dd className="mt-1 text-2xl text-foreground tabular-nums">{filled}</dd>
              </div>
              <div>
                <dt className="eyebrow">Fill rate</dt>
                <dd
                  className={cn(
                    "mt-1 text-2xl tabular-nums",
                    fillRate > 0 ? "text-success" : "text-foreground",
                  )}
                >
                  {Math.round(fillRate)}%
                </dd>
              </div>
            </dl>
            <Progress
              value={fillRate}
              className="mt-4"
              aria-label={`Fill rate ${Math.round(fillRate)}%`}
            />
          </SectionCard>

          <SectionCard title="Contact information" className="h-auto" noPadding>
            <ul className="divide-y divide-border">
              {[
                { icon: <User />, label: "Contact person", value: p.contact_person || "Not specified" },
                { icon: <Phone />, label: "Phone", value: p.contact_phone || "Not specified" },
                { icon: <Mail />, label: "Email", value: p.contact_email || p.email },
              ].map((row) => (
                <li key={row.label} className="flex items-center gap-3 px-4 py-3 md:px-5">
                  <span className="text-muted-foreground [&_svg]:size-4" aria-hidden>
                    {row.icon}
                  </span>
                  <span className="sr-only">{row.label}: </span>
                  <span className="min-w-0 truncate text-sm text-foreground">{row.value}</span>
                </li>
              ))}
            </ul>
          </SectionCard>
        </aside>
      </div>

      {/* Reject dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={(o) => !o && setRejectDialogOpen(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject partner</DialogTitle>
            <DialogDescription>
              Are you sure you want to reject{" "}
              <span className="font-medium text-foreground">{p.name}</span>?
            </DialogDescription>
          </DialogHeader>
          <Field label="Reason (optional)">
            <Textarea
              rows={2}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </Field>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={rejectPartner.isPending}
              loading={rejectPartner.isPending}
              onClick={handleReject}
            >
              {rejectPartner.isPending ? "Rejecting..." : "Reject partner"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit profile dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(o) => !o && setEditDialogOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit partner profile</DialogTitle>
            <DialogDescription>Update the organization profile and contact details.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            {editFields.map((f) => (
              <Field key={f.key} label={f.label}>
                <Input
                  value={editData[f.key] || ""}
                  onChange={(e) => setEditData((d) => ({ ...d, [f.key]: e.target.value }))}
                />
              </Field>
            ))}
            <Field label="Description" className="sm:col-span-2">
              <Textarea
                rows={3}
                value={editData.description || ""}
                onChange={(e) => setEditData((d) => ({ ...d, description: e.target.value }))}
              />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={updatePartner.isPending}
              loading={updatePartner.isPending}
              onClick={handleSaveEdit}
            >
              {updatePartner.isPending ? "Saving..." : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Slot create/edit dialog */}
      <Dialog open={slotDialogOpen} onOpenChange={(o) => !o && setSlotDialogOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingSlot ? "Edit placement slot" : "Add placement slot"}</DialogTitle>
            <DialogDescription>
              Define the role, requirements and capacity for this partner.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Slot title" required className="sm:col-span-2">
              <Input
                placeholder="e.g. Software Engineering Intern"
                value={slotForm.title}
                onChange={(e) => setSlotForm((f) => ({ ...f, title: e.target.value }))}
              />
            </Field>
            <Field label="Description" className="sm:col-span-2">
              <Textarea
                rows={2}
                placeholder="Describe the role and responsibilities..."
                value={slotForm.description}
                onChange={(e) => setSlotForm((f) => ({ ...f, description: e.target.value }))}
              />
            </Field>
            <Field label="Requirements" className="sm:col-span-2">
              {({ id, describedBy }) => (
                <MultiCombobox
                  id={id}
                  aria-describedby={describedBy}
                  options={skillNames}
                  value={slotForm.requirements.filter((r) => skillNames.includes(r))}
                  onChange={(names) => setSlotForm((f) => ({ ...f, requirements: names }))}
                  placeholder="Select required skills"
                  searchPlaceholder="Search skills…"
                  emptyText="No skills found"
                />
              )}
            </Field>
            <Field label="Location">
              {({ id }) => (
                <Select
                  value={slotForm.location || undefined}
                  onValueChange={(v) => setSlotForm((f) => ({ ...f, location: v }))}
                >
                  <SelectTrigger id={id}>
                    <SelectValue placeholder="Select region" />
                  </SelectTrigger>
                  <SelectContent>
                    {regions?.map((r: string) => (
                      <SelectItem key={r} value={r}>
                        {r}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Duration (hours)">
              <Input
                type="number"
                min={1}
                placeholder="e.g. 480"
                value={slotForm.duration}
                onChange={(e) => setSlotForm((f) => ({ ...f, duration: e.target.value }))}
              />
            </Field>
            <Field label="Work arrangement">
              {({ id }) => (
                <Select
                  value={slotForm.arrangement}
                  onValueChange={(v) => setSlotForm((f) => ({ ...f, arrangement: v }))}
                >
                  <SelectTrigger id={id}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="on-site">On-site</SelectItem>
                    <SelectItem value="remote">Remote</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>
            <Field label="Max students">
              <Input
                type="number"
                min={1}
                value={slotForm.max_students}
                onChange={(e) => setSlotForm((f) => ({ ...f, max_students: parseInt(e.target.value) || 1 }))}
              />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSlotDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!slotForm.title || createSlot.isPending || updateSlot.isPending}
              loading={createSlot.isPending || updateSlot.isPending}
              onClick={handleSaveSlot}
            >
              {createSlot.isPending || updateSlot.isPending
                ? "Saving..."
                : editingSlot
                  ? "Update slot"
                  : "Create slot"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
